import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreviewActions } from "@/lib/usePlatformDesignerBuyerPreview";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { CLIENT_MODULE_SCOPES } from "@/lib/clientModuleScope";
import { usePersistedClientId } from "@/lib/clientSelection";
import { useEntitlements } from "@/modules/useEntitlements";
import { canInstallLicenseForWorkspace } from "@/lib/accountStatus";
import InstallationLicenseBar from "@/components/InstallationLicenseBar";
import PanelHubClientSelector, { setHubDefaultClientId } from "@/components/PanelHubClientSelector";
import PanelLicenseActivationBlock from "@/components/PanelLicenseActivationBlock";
import PlatformDesignerBuyerPreviewHeader from "@/components/PlatformDesignerBuyerPreviewHeader";

/**
 * Meta del Panel General: licencia / prueba y cliente predeterminado (debajo de la barra de sesión).
 */
export default function PanelHubHeaderMeta({ showClientSelector = true }) {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const { buyerPreviewActive, enableBuyerPreview } = usePlatformDesignerBuyerPreviewActions();
  const { mode, canSwitch } = useWorkspaceMode();
  const [clients, setClients] = useState([]);
  const { systemInfo, reload: refreshSystemInfo, hasEntitlement, modulo_gestion_despacho, modulo_visor_sat } =
    useEntitlements({});

  const trialPreferRfc =
    systemInfo?.subscription?.mode === "trial"
      ? (systemInfo?.profile?.rfc || "").trim().toUpperCase()
      : "";

  const [clientId, setClientId] = usePersistedClientId(clients, {
    moduleScope: CLIENT_MODULE_SCOPES.VISOR_SAT,
    autoPickFirst: false,
    preferRfc: trialPreferRfc,
  });

  const loadClients = useCallback(() => {
    api
      .get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    const onOperationalReset = () => {
      loadClients();
      refreshSystemInfo();
    };
    window.addEventListener("visor:operational-data-reset", onOperationalReset);
    return () => window.removeEventListener("visor:operational-data-reset", onOperationalReset);
  }, [loadClients, refreshSystemInfo]);

  const isDistributorWorkspace =
    !isPlatformDesigner
    && canSwitch
    && mode === "distribuidor"
    && Boolean(user?.can_generate_licenses);

  const handleClientSelect = useCallback(
    (id) => {
      setHubDefaultClientId(id);
      setClientId(id);
    },
    [setClientId],
  );

  const showClients = showClientSelector && !isDistributorWorkspace && !buyerPreviewActive;
  const hasDespacho = Boolean(modulo_gestion_despacho || hasEntitlement("despacho"));
  const hasVisor = Boolean(modulo_visor_sat || hasEntitlement("visor"));
  const licenseInactive =
    systemInfo?.subscription?.mode === "trial"
    && !systemInfo?.subscription?.ok
    && systemInfo?.subscription?.reason === "trial_expired";

  const licenseBlock = useMemo(() => {
    if (isPlatformDesigner && buyerPreviewActive) {
      return <PlatformDesignerBuyerPreviewHeader baseSystemInfo={systemInfo} />;
    }
    if (!systemInfo) {
      return (
        <div
          className="h-full rounded-sm border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 animate-pulse"
          data-testid="panel-hub-license-loading"
        >
          Cargando estado de licencia e instalación…
        </div>
      );
    }
    return (
      <InstallationLicenseBar
        info={systemInfo}
        distributorWorkspace={isDistributorWorkspace}
        canRequestLicense={canInstallLicenseForWorkspace(user)}
      />
    );
  }, [systemInfo, isDistributorWorkspace, user, isPlatformDesigner, buyerPreviewActive]);

  return (
    <div
      className={`relative z-0 w-full min-w-0 overflow-visible border-b border-slate-100 bg-white px-4 sm:px-8 ${
        buyerPreviewActive ? "pb-2 space-y-2" : "pb-4 space-y-3"
      }`}
      data-testid="panel-hub-header-meta"
    >
      <div
        className={`grid w-full min-w-0 gap-3 items-stretch ${
          showClients ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        }`}
        data-testid="panel-hub-header-cards-row"
      >
        {showClients ? (
          <div className="min-w-0 h-full">
            <PanelHubClientSelector
              clients={clients}
              clientId={clientId}
              onSelect={handleClientSelect}
              hasDespacho={hasDespacho}
              hasVisor={hasVisor}
              licenseInactive={licenseInactive}
            />
          </div>
        ) : null}
        <div className="min-w-0 h-full">{licenseBlock}</div>
      </div>
      {isPlatformDesigner && !buyerPreviewActive ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs border-sky-300 text-sky-900 hover:bg-sky-50"
            onClick={() => enableBuyerPreview()}
            data-testid="platform-designer-buyer-preview-enter"
          >
            Ver como comprador
          </Button>
        </div>
      ) : null}
      {!buyerPreviewActive ? <PanelLicenseActivationBlock /> : null}
    </div>
  );
}
